import {
    getNumAccountsForCategory,
    MoneyMapAccountRule,
    MoneyMapCategoryHeader,
    MoneyMapIncomeAccount,
    prioritySortRulesAndGroups,
} from '@fruitful/money-map';
import { MoneyMapRuleGroup } from '@fruitful/money-map/src/components/MoneyMapRuleGroup';
import {
    type DeepPartial,
    type MoneyMapAccountWithAccountDetails,
    MoneyMapCategory,
    type MoneyMapRuleGroup as MoneyMapRuleGroupType,
    type MoneyMapRuleWithAccountDetails,
    type MoneyMapWithRulesAndAccounts,
} from '@fruitful/types';
import { keyBy, sortBy } from 'lodash';
import {
    Button,
    ButtonSize,
    ButtonVariant,
    PlusIcon,
    XStack,
    YStack,
} from 'smoothie';

import { useMoneyMapAccounts } from '../../providers/MoneyMapAccountsProvider';
import { isGoalRuleMissingPositiveTargetBalance } from '../../utils/moneyMapUtils';

interface EditorSectionContainerProps {
    category: MoneyMapCategory;
    amountCents: number | null;
    incomeAccount?: DeepPartial<MoneyMapAccountWithAccountDetails>;
    onPressIncomeAccount?: () => void;
    rules?: Array<DeepPartial<MoneyMapRuleWithAccountDetails>>;
    ctaButtonText: string;
    onPressCta: () => void;
    hideButtonIcon?: boolean;
    onPressRule?: (ruleId: string) => void;
    onPressGroup?: (groupId: string) => void;
    moneyMap: DeepPartial<MoneyMapWithRulesAndAccounts>;
    disabled?: boolean;
    onPressAddGroup?: () => void;
    onPressAddGoalToGroup?: (groupId: string) => void;
    groups?: Array<DeepPartial<MoneyMapRuleGroupType>>;
}

const EditorSectionContainer = ({
    category,
    amountCents,
    incomeAccount,
    onPressIncomeAccount,
    rules,
    ctaButtonText,
    onPressCta,
    hideButtonIcon = false,
    onPressRule,
    onPressGroup,
    moneyMap,
    disabled = false,
    onPressAddGroup,
    onPressAddGoalToGroup,
    groups,
}: EditorSectionContainerProps) => {
    const { accounts } = useMoneyMapAccounts();
    const accountsById = keyBy(accounts, 'id');

    const numAccounts = getNumAccountsForCategory(
        category,
        incomeAccount,
        rules,
    );

    const sortedRulesAndGroups = prioritySortRulesAndGroups(
        rules ?? [],
        groups ?? [],
    );

    const showAddGroupButton = onPressAddGroup != null;
    const showAddButton = category !== MoneyMapCategory.GOALS;

    return (
        <YStack
            borderWidth={1}
            borderColor="$gray600"
            borderStyle="dashed"
            borderRadius={24}
            backgroundColor="$white"
            padding={16}
            gap={16}
            width={420}
            minHeight={112}
        >
            <MoneyMapCategoryHeader
                category={category}
                numAccounts={numAccounts}
                totalAmountCents={amountCents}
            />
            {category === MoneyMapCategory.INCOME && incomeAccount != null && (
                <MoneyMapIncomeAccount
                    account={incomeAccount}
                    onPress={onPressIncomeAccount}
                />
            )}
            {sortedRulesAndGroups.map(({ entityType, ...item }, index) => {
                if (entityType === 'group') {
                    const groupRules = sortBy(
                        (rules ?? []).filter(
                            (r) => r.moneyMapRuleGroupId === item.id,
                        ),
                        'priority',
                    );

                    return (
                        <MoneyMapRuleGroup
                            key={index}
                            category={category}
                            group={item as DeepPartial<MoneyMapRuleGroupType>}
                            rules={groupRules}
                            moneyMap={moneyMap}
                            accountsById={accountsById}
                            isRuleInvalidGoalTarget={
                                isGoalRuleMissingPositiveTargetBalance
                            }
                            onPressEdit={() => {
                                if (item.id != null) {
                                    onPressGroup?.(item.id);
                                }
                            }}
                            onPressAddGoal={() => {
                                if (item.id != null) {
                                    onPressAddGoalToGroup?.(item.id);
                                }
                            }}
                            onPressRule={(identifier: string) => {
                                onPressRule?.(identifier);
                            }}
                        />
                    );
                }

                return (
                    <MoneyMapAccountRule
                        key={index}
                        category={category}
                        rule={
                            item as DeepPartial<MoneyMapRuleWithAccountDetails>
                        }
                        moneyMap={moneyMap}
                        accountsById={accountsById}
                        showInvalidGoalTargetBalance={isGoalRuleMissingPositiveTargetBalance(
                            item as DeepPartial<MoneyMapRuleWithAccountDetails>,
                        )}
                        onPress={() => {
                            if (item.id != null) {
                                onPressRule?.(item.id);
                            } else if (item.description != null) {
                                onPressRule?.(item.description);
                            }
                        }}
                    />
                );
            })}
            <XStack alignSelf="flex-start" gap={12}>
                {showAddButton && (
                    <Button
                        text={ctaButtonText}
                        variant={ButtonVariant.SECONDARY}
                        size={ButtonSize.TINY}
                        icon={hideButtonIcon ? undefined : PlusIcon}
                        iconPosition="left"
                        width="auto"
                        onPress={onPressCta}
                        disabled={disabled}
                    />
                )}
                {showAddGroupButton && (
                    <Button
                        text="Add Set"
                        variant={ButtonVariant.SECONDARY}
                        size={ButtonSize.TINY}
                        icon={PlusIcon}
                        iconPosition="left"
                        width="auto"
                        onPress={onPressAddGroup}
                        disabled={disabled}
                    />
                )}
            </XStack>
        </YStack>
    );
};

export default EditorSectionContainer;
